import React, { useEffect, useMemo, useRef, useState } from "react";
import CRMNavigation from "@/components/CRMNavigation";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";

function StaggerList({
  items,
  render,
  baseDelay = 0,
  step = 90,
}: {
  items: any[];
  render: (item: any, index: number) => React.ReactNode;
  baseDelay?: number;
  step?: number;
}) {
  return (
    <>
      {items.map((it, i) => (
        <Reveal key={it?.title ?? it?.id ?? i} delay={baseDelay + i * step} variant="up">
          {render(it, i)}
        </Reveal>
      ))}
    </>
  );
}

export default function CRMPage() {
  const features = useMemo(
    () => [
      {
        icon: "🧭",
        title: "Powerful CRM Core",
        desc: "Accounts, contacts, opportunities, activities, and pipelines designed for speed.",
      },
      {
        icon: "📁",
        title: "Project & Document Hub",
        desc: "Files, notes, approvals, and tasks in one place with version history.",
      },
      {
        icon: "💬",
        title: "Social & Omni-channel",
        desc: "Email, WhatsApp, and web chat integrated — keep context across touchpoints.",
      },
      {
        icon: "📊",
        title: "Business Overview",
        desc: "KPIs, forecasts, and revenue tracking with custom dashboards.",
      },
      {
        icon: "📱",
        title: "Works on Mobile",
        desc: "Responsive by default and companion mobile apps for on-the-go updates.",
      },
      {
        icon: "🔑",
        title: "Key Management",
        desc: "Roles, permissions, and approval flows to match your org structure.",
      },
    ],
    []
  );

  const taskBullets = useMemo(
    () => [
      "Set date and time for task reminder",
      "Set task title and lead",
      "Assign the task and add observers",
      "Mark task active/inactive",
    ],
    []
  );

  const benefitBullets = useMemo(
    () => [
      "Expanded sales through better planning and forecasting",
      "Recognize needs by understanding customer requirements",
      "Upsell/cross-sell by suggesting relevant upgrades",
      "Identify profitable customers and focus efforts",
    ],
    []
  );

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: "'Roboto', sans-serif" }}>

      <CRMNavigation />

      {/* =========================
          SECTION 1: HERO (DARK)
      ========================== */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#040814] text-white">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_20%,rgba(59,130,246,0.18),transparent_55%),radial-gradient(900px_circle_at_80%_40%,rgba(34,211,238,0.12),transparent_55%),radial-gradient(900px_circle_at_50%_90%,rgba(99,102,241,0.14),transparent_60%)]" />
          <div className="absolute inset-0 opacity-40 [background-image:linear-gradient(to_right,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:48px_48px]" />
        </div>

        <div className="relative container mx-auto px-6 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div className="space-y-6">
              <Reveal variant="left" delay={0}>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold text-white ring-1 ring-white/15 backdrop-blur">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                  New in VSCRM · Smart Dashboards
                </div>
              </Reveal>

              <Reveal variant="up" delay={80}>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
                  CRM software that lets you{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-300 to-indigo-400">
                    close more deals
                  </span>{" "}
                  in less time
                </h1>
              </Reveal>

              <Reveal variant="up" delay={140}>
                <p className="text-white/75 text-base sm:text-lg leading-relaxed max-w-xl">
                  Automate follow-ups, qualify leads, and get a 360° overview of your pipeline with a modern, fast,
                  and premium CRM built for growing teams.
                </p>
              </Reveal>

              <Reveal variant="up" delay={210}>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <a
                    href="/getintouch"
                    className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-3 text-white font-semibold shadow-lg shadow-blue-600/20 hover:bg-blue-500 transition"
                  >
                    Book a Live Demo
                  </a>
                  <a
                    href="#features"
                    className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl bg-white/10 px-6 py-3 text-white font-semibold ring-1 ring-white/15 hover:bg-white/15 transition backdrop-blur"
                  >
                    Explore Features
                  </a>
                </div>
              </Reveal>
            </div>

            {/* Right image */}
            <Reveal variant="right" delay={120} className="lg:justify-self-end">
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-blue-500/20 via-cyan-400/10 to-indigo-500/20 blur-2xl" />
                <div className="relative rounded-3xl bg-white/5 ring-1 ring-white/10 p-2 backdrop-blur shadow-2xl">
                  <img
                    src="/crm-banner.png"
                    alt="VSCRM Dashboard"
                    className="w-full max-w-lg lg:max-w-md rounded-2xl object-cover"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* =========================
          SECTION 2: FEATURES (LIGHT)
      ========================== */}
      <section id="features" className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6">
          <Reveal as="header" variant="up" className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
              Fully Featured CRM
            </h2>
            <p className="mt-3 text-slate-600">
              Everything your team needs to capture, qualify, collaborate, and close — without the clutter.
            </p>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <StaggerList
              items={features}
              baseDelay={80}
              step={90}
              render={(f: any) => (
                <article className="group rounded-2xl bg-white p-6 ring-1 ring-slate-200 shadow-sm hover:shadow-xl transition overflow-hidden relative">
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition bg-gradient-to-br from-blue-50 via-white to-indigo-50" />
                  <div className="relative">
                    <div className="h-11 w-11 rounded-2xl bg-blue-50 text-blue-700 grid place-items-center ring-1 ring-blue-100">
                      <span className="text-lg">{f.icon}</span>
                    </div>
                    <h3 className="mt-4 font-bold text-slate-900">{f.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>

                    <div className="mt-5 h-1 w-16 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 opacity-70 group-hover:w-28 transition-all" />
                  </div>
                </article>
              )}
            />
          </div>
        </div>
      </section>

      {/* =========================
          SECTION 3: DASHBOARD (DARK)
      ========================== */}
      <section id="dashboard" className="py-16 lg:py-24 bg-[#040814] text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_20%,rgba(59,130,246,0.14),transparent_55%),radial-gradient(900px_circle_at_80%_40%,rgba(34,211,238,0.10),transparent_55%),radial-gradient(900px_circle_at_50%_90%,rgba(99,102,241,0.12),transparent_60%)]" />
          <div className="absolute inset-0 opacity-30 [background-image:linear-gradient(to_right,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:54px_54px]" />
        </div>

        <div className="relative container mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="left" className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold ring-1 ring-white/15">
              📈 Dashboard Insights
            </div>
            <h3 className="mt-4 text-xl sm:text-2xl font-extrabold">Dashboard</h3>
            <p className="mt-3 text-white/75 leading-relaxed">
              Our CRM dashboard is designed to let our clients perform key activities and tasks with a single click.
              It shows an outline of total leads, overall collection, and revenue. You can also track lead status in a
              clean graphical format.
            </p>
          </Reveal>

          <Reveal variant="right" className="order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-blue-500/20 via-cyan-400/10 to-indigo-500/20 blur-2xl" />
              <div className="relative rounded-3xl bg-white/5 p-2 ring-1 ring-white/10 backdrop-blur shadow-2xl">
                <img src="/Dashboard.png" alt="Dashboard preview" className="rounded-2xl object-cover w-full" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =========================
          SECTION 4: TEAM STRUCTURE (LIGHT)
      ========================== */}
      <section id="teams" className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="left">
            <div className="rounded-3xl bg-white p-2 shadow-lg ring-1 ring-slate-200">
              <img src="/Customized-Lead.png" alt="Leads preview" className="rounded-2xl object-cover w-full" />
            </div>
          </Reveal>

          <Reveal variant="right">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-slate-200">
              👥 Roles & Access
            </div>

            <h3 className="mt-4 text-xl sm:text-2xl font-extrabold text-slate-900">Team Structure</h3>

            <p className="mt-3 text-slate-600 leading-relaxed">
              CRM Admin has full access over the entire process. Admin can create users, define access for ABM and ABE,
              customize report status, and create branch & source.
            </p>

            <div className="mt-5 rounded-2xl bg-gradient-to-br from-blue-50 via-white to-indigo-50 ring-1 ring-slate-200 p-5">
              <h4 className="font-bold text-blue-700">Admin can create 2 sub-admin (User)</h4>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                <Reveal as="li" variant="up" delay={80} className="flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-blue-600" />
                  Area Business Manager (ABM)
                </Reveal>
                <Reveal as="li" variant="up" delay={160} className="flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-indigo-600" />
                  Area Business Executive (ABE)
                </Reveal>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =========================
          SECTION 5: TASK (DARK)
      ========================== */}
      <section id="task" className="py-16 lg:py-24 bg-[#040814] text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_20%,rgba(59,130,246,0.12),transparent_55%),radial-gradient(900px_circle_at_80%_40%,rgba(34,211,238,0.10),transparent_55%),radial-gradient(900px_circle_at_50%_90%,rgba(99,102,241,0.10),transparent_60%)]" />
          <div className="absolute inset-0 opacity-25 [background-image:linear-gradient(to_right,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:56px_56px]" />
        </div>

        <div className="relative container mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="left" className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold ring-1 ring-white/15">
              ✅ Productivity
            </div>

            <h3 className="mt-4 text-xl sm:text-2xl font-extrabold">Task Management</h3>
            <p className="mt-3 text-white/75 leading-relaxed">
              Add tasks to remind the team about activities with leads, contacts, and organizations. Create tasks quickly
              from lead cards and assign with observers.
            </p>

            <div className="mt-5 rounded-2xl bg-white/5 ring-1 ring-white/10 p-5">
              <h4 className="font-semibold text-cyan-200">While adding Task, follow:</h4>
              <ul className="mt-3 space-y-2 text-sm text-white/80">
                <StaggerList
                  items={taskBullets}
                  baseDelay={120}
                  step={80}
                  render={(x: string) => (
                    <li className="flex gap-2">
                      <span className="mt-1.5 h-2 w-2 rounded-full bg-cyan-300" />
                      {x}
                    </li>
                  )}
                />
              </ul>
            </div>
          </Reveal>

          <Reveal variant="right" className="order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-blue-500/20 via-cyan-400/10 to-indigo-500/20 blur-2xl" />
              <div className="relative rounded-3xl bg-white/5 p-2 ring-1 ring-white/10 backdrop-blur shadow-2xl">
                <img src="/add-task.png" alt="Task preview" className="rounded-2xl object-cover w-full" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =========================
          SECTION 6: COMPANY DETAILS (LIGHT)
      ========================== */}
      <section id="CompanyDetails" className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="left">
            <div className="rounded-3xl bg-white p-2 shadow-lg ring-1 ring-slate-200">
              <img
                src="/add-company.png"
                alt="Company details preview"
                className="rounded-2xl object-cover w-full"
              />
            </div>
          </Reveal>

          <Reveal variant="right">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-slate-200">
              🏢 Setup
            </div>

            <h3 className="mt-4 text-xl sm:text-2xl font-extrabold text-slate-900">Company Details</h3>

            <p className="mt-3 text-slate-600 leading-relaxed">
              Company-level settings help standardize essential features across all users under your organization. Admin
              can configure company details for consistent operations.
            </p>

            <div className="mt-5 rounded-2xl bg-gradient-to-br from-slate-50 via-white to-blue-50 ring-1 ring-slate-200 p-5">
              <h4 className="font-bold text-blue-700">Steps for Accessing the Company Details</h4>
              <ol className="mt-3 space-y-2 text-sm text-slate-700 list-decimal pl-5">
                <Reveal as="li" delay={80} variant="up">
                  Log in to Variable Soft CRM with Administrator rights.
                </Reveal>
                <Reveal as="li" delay={140} variant="up">
                  Go to General &gt; Add Company
                </Reveal>
                <Reveal as="li" delay={200} variant="up">
                  Click on the new Company Details icon
                </Reveal>
                <Reveal as="li" delay={260} variant="up">
                  Fill the company details in the pop-up
                </Reveal>
                <Reveal as="li" delay={320} variant="up">
                  Finally, click on Add Company
                </Reveal>
              </ol>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =========================
          SECTION 7: CRM BENEFITS (DARK)
      ========================== */}
      <section id="crmBenefits" className="py-16 lg:py-24 bg-[#040814] text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_20%,rgba(16,185,129,0.10),transparent_55%),radial-gradient(900px_circle_at_80%_40%,rgba(59,130,246,0.12),transparent_55%),radial-gradient(900px_circle_at_50%_90%,rgba(99,102,241,0.12),transparent_60%)]" />
          <div className="absolute inset-0 opacity-25 [background-image:linear-gradient(to_right,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:58px_58px]" />
        </div>

        <div className="relative container mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="left" className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold ring-1 ring-white/15">
              ⭐ Value
            </div>

            <h3 className="mt-4 text-xl sm:text-2xl font-extrabold">Benefits Of CRM With No Limits</h3>

            <p className="mt-3 text-white/75 leading-relaxed">
              Implementing a CRM may take time and cost, but it unlocks strong growth through structured processes,
              better forecasting, and smarter customer handling.
            </p>

            <div className="mt-6 grid gap-3">
              <StaggerList
                items={benefitBullets}
                baseDelay={120}
                step={90}
                render={(x: string, i: number) => (
                  <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
                    <div className="flex gap-3">
                      <div className="h-9 w-9 rounded-xl bg-white/10 grid place-items-center ring-1 ring-white/10">
                        ✅
                      </div>
                      <p className="text-sm text-white/80 leading-relaxed">{x}</p>
                    </div>
                  </div>
                )}
              />
            </div>
          </Reveal>

          <Reveal variant="right" className="order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-emerald-500/10 via-blue-500/10 to-indigo-500/20 blur-2xl" />
              <div className="relative rounded-3xl bg-white/5 p-2 ring-1 ring-white/10 backdrop-blur shadow-2xl">
                <img src="/benefit-crm.png" alt="Benefits preview" className="rounded-2xl object-cover w-full" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
